import React, { useState, useMemo, useEffect, useCallback } from 'react';
import StudentNavigation from '../../components/ui/StudentNavigation';
import NotificationPanel from '../../components/ui/NotificationPanel';
import SessionStatusIndicator from '../../components/ui/SessionStatusIndicator';
import InstituteCard from './components/InstituteCard';
import FilterToolbar from './components/FilterToolbar';
import SavedInstitutesPanel from './components/SavedInstitutesPanel';
import UpcomingSessionsPanel from './components/UpcomingSessionsPanel';
import AnnouncementsPanel from './components/AnnouncementsPanel';
import SortingControls from './components/SortingControls';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { instituteService } from '../../services/instituteService';
import { sessionService } from '../../services/sessionService';
import { notificationService } from '../../services/notificationService';
import { announcementService } from '../../services/announcementService';
import { errorHandler } from '../../utils/errorHandler';
import { performanceUtils } from '../../utils/performance';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const StudentDashboard = () => {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid');
  const [savedInstituteIds, setSavedInstituteIds] = useState([]);
  const [savedInstitutesData, setSavedInstitutesData] = useState([]);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [institutes, setInstitutes] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [checkingApproval, setCheckingApproval] = useState(true);

  // Check approval status on mount
  useEffect(() => {
    checkApprovalStatus();
  }, [user]);

  const checkApprovalStatus = async () => {
    try {
      if (!user?.id) {
        navigate('/student-registration');
        return;
      }

      const { data, error } = await supabase?.from('student_profiles')?.select('approval_status')?.eq('user_id', user?.id)?.maybeSingle();

      if (error) throw error;

      if (!data) {
        navigate('/student-registration');
        return;
      }

      const status = data?.approval_status || 'pending';
      setApprovalStatus(status);

      if (status !== 'approved') {
        navigate('/pending-approval');
        return;
      }
    } catch (error) {
      console.error('Error checking approval status:', error);
      toast?.error('Failed to verify account status');
    } finally {
      setCheckingApproval(false);
    }
  };

  // Debounced search handler
  const debouncedSearch = useCallback(
    performanceUtils?.debounce((query) => {
      console.log('Search query:', query);
    }, 300),
    []
  );

  // Initial load - load all data once
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load approved institutes from Supabase
      const institutesData = await errorHandler?.withRetry(
        async () => instituteService?.getInstitutes({
          category: selectedCategory,
          location: selectedLocation
        }),
        {
          maxRetries: 2,
          retryDelay: 1000,
          onRetry: (attempt) => {
            toast?.info(`Loading data... (Attempt ${attempt}/2)`);
          }
        }
      );
      setInstitutes(institutesData || []);

      // Load real announcements from Supabase
      const announcementsData = await announcementService?.getAnnouncements();
      setAnnouncements(announcementsData || []);

      // Load real notifications if user exists
      if (user?.id) {
        try {
          const notificationsData = await notificationService?.getNotifications(user?.id);
          setNotifications(notificationsData || []);
        } catch (notifErr) {
          console.error('Error loading notifications:', notifErr);
          setNotifications([]);
        }
      }

      setRetryCount(0);
    } catch (err) {
      const appError = errorHandler?.parseSupabaseError(err);
      setError(appError?.message);
      toast?.error(appError?.message);
      errorHandler?.logError(appError, { context: 'Student Dashboard Load' });
      setInstitutes([]);
      setAnnouncements([]);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  // Load institutes when filters change (after initial load)
  useEffect(() => {
    if (loading) return;

    const loadInstitutes = async () => {
      try {
        const data = await instituteService?.getInstitutes({
          category: selectedCategory,
          location: selectedLocation
        });
        setInstitutes(data || []);
      } catch (err) {
        const appError = errorHandler?.parseSupabaseError(err);
        setError(appError?.message);
        toast?.error(appError?.message);
        setInstitutes([]);
      }
    };

    loadInstitutes();
  }, [selectedCategory, selectedLocation, loading]);

  // Load saved institutes from Supabase
  useEffect(() => {
    if (!user?.id) return;

    const loadSavedInstitutes = async () => {
      try {
        const data = await instituteService?.getSavedInstitutes(user?.id);
        const savedList = data || [];
        // Store full saved institute objects for the panel
        const mappedSaved = savedList?.map((inst) => ({
          id: inst?.id,
          name: inst?.institute_name || inst?.instituteName || inst?.name || 'Unknown Institute',
          logo: inst?.logo_url || inst?.logoUrl || inst?.logo || null,
          logoAlt: `${inst?.institute_name || inst?.name || 'Institute'} logo`,
          location: inst?.user?.city
            ? `${inst?.user?.city}${inst?.user?.state ? ', ' + inst?.user?.state : ''}`
            : inst?.location || 'Location not specified',
        }));
        setSavedInstitutesData(mappedSaved);
        setSavedInstituteIds(savedList?.map((inst) => inst?.id) || []);
      } catch (err) {
        console.error('Error loading saved institutes:', err);
        setSavedInstitutesData([]);
        setSavedInstituteIds([]);
      }
    };

    loadSavedInstitutes();
  }, [user?.id]);

  // Load upcoming counseling sessions from Supabase
  useEffect(() => {
    if (!user?.id) return;

    const loadSessions = async () => {
      try {
        const data = await sessionService?.getUpcomingSessions(user?.id, 'student');
        setUpcomingSessions(data || []);
      } catch (err) {
        console.error('Error loading sessions:', err);
        setUpcomingSessions([]);
      }
    };

    loadSessions();
  }, [user?.id]);

  const handleToggleSave = async (instituteId) => {
    if (!user?.id) {
      toast?.warning('Please login to save institutes');
      return;
    }

    try {
      if (savedInstituteIds?.includes(instituteId)) {
        await instituteService?.unsaveInstitute(user?.id, instituteId);
        setSavedInstituteIds((prev) => prev?.filter((id) => id !== instituteId));
        setSavedInstitutesData((prev) => prev?.filter((inst) => inst?.id !== instituteId));
        toast?.success('Institute removed from saved list');
      } else {
        await instituteService?.saveInstitute(user?.id, instituteId);
        setSavedInstituteIds((prev) => [...prev, instituteId]);
        // Find the institute in the current list and add to saved
        const instituteToSave = institutes?.find((inst) => inst?.id === instituteId);
        if (instituteToSave) {
          const mappedInst = {
            id: instituteToSave?.id,
            name: instituteToSave?.instituteName || instituteToSave?.name || 'Unknown Institute',
            logo: instituteToSave?.logoUrl || instituteToSave?.logo || null,
            logoAlt: `${instituteToSave?.instituteName || instituteToSave?.name || 'Institute'} logo`,
            location: instituteToSave?.user?.city
              ? `${instituteToSave?.user?.city}${instituteToSave?.user?.state ? ', ' + instituteToSave?.user?.state : ''}`
              : 'Location not specified',
          };
          setSavedInstitutesData((prev) => [...prev, mappedInst]);
        }
        toast?.success('Institute saved successfully');
      }
    } catch (err) {
      const appError = errorHandler?.parseSupabaseError(err);
      toast?.error(appError?.message);
      errorHandler?.logError(appError, { context: 'Toggle Save Institute', instituteId });
    }
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    loadInitialData();
  };

  const filteredInstitutes = useMemo(() => {
    return institutes?.filter((institute) => {
      const name = institute?.instituteName || institute?.name || '';
      const courses = institute?.courses || [];
      const matchesSearch = name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        courses?.some((course) => (course?.course_name || course?.category || '')?.toLowerCase()?.includes(searchQuery?.toLowerCase()));
      const matchesCourse = selectedCourse === 'all' || courses?.some((course) => (course?.category || '') === selectedCourse);
      const location = institute?.user?.city || institute?.location || '';
      const matchesLocation = selectedLocation === 'all' || location?.toLowerCase()?.includes(selectedLocation?.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || courses?.some((course) => (course?.category || '') === selectedCategory);
      return matchesSearch && matchesCourse && matchesLocation && matchesCategory;
    });
  }, [institutes, searchQuery, selectedCourse, selectedLocation, selectedCategory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const handleRemoveSaved = async (id) => {
    if (!user?.id) return;
    try {
      await instituteService?.unsaveInstitute(user?.id, id);
      setSavedInstituteIds((prev) => prev?.filter((i) => i !== id));
      setSavedInstitutesData((prev) => prev?.filter((inst) => inst?.id !== id));
      toast?.success('Institute removed from saved list');
    } catch (err) {
      const appError = errorHandler?.parseSupabaseError(err);
      toast?.error(appError?.message || 'Failed to remove institute');
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCourse('all');
    setSelectedLocation('all');
    setSelectedCategory('all');
    setSelectedStatus('all');
  };

  const handleMarkNotificationAsRead = async (id) => {
    try {
      await notificationService?.markAsRead(id);
      setNotifications((prev) =>
        prev?.map((n) => n?.id === id ? { ...n, isRead: true } : n)
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleClearAllNotifications = async () => {
    if (!user?.id) return;
    try {
      await notificationService?.markAllAsRead(user?.id);
      setNotifications((prev) => prev?.map((n) => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Error clearing notifications:', err);
    }
  };

  // Show loading while checking approval
  if (checkingApproval) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Icon name="Loader" className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  // Only render dashboard if approved
  if (approvalStatus !== 'approved') {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <StudentNavigation />
      <main className="pt-[60px]">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 md:gap-6 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                Discover Your Future
              </h1>
              <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
                Explore participating institutes and connect with admission counselors
              </p>
            </div>

            <div className="flex items-center gap-3 w-full lg:w-auto">
              <NotificationPanel userId={user?.id} />
              <SessionStatusIndicator
                hasActiveSession={false}
                onChatClick={() => console.log('Chat clicked')}
                onVideoClick={() => console.log('Video clicked')}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
            <div className="lg:col-span-8">
              <FilterToolbar
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                selectedCourse={selectedCourse}
                onCourseChange={setSelectedCourse}
                selectedLocation={selectedLocation}
                onLocationChange={setSelectedLocation}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                selectedStatus={selectedStatus}
                onStatusChange={setSelectedStatus}
                resultCount={filteredInstitutes?.length}
                onClearFilters={handleClearFilters}
                isMobileFilterOpen={isMobileFilterOpen}
                onToggleMobileFilter={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
              />

              <SortingControls
                sortBy={sortBy}
                onSortChange={setSortBy}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />

              {filteredInstitutes?.length === 0 ? (
                <div className="bg-card border border-border rounded-lg p-12 md:p-16 text-center">
                  <div className="w-20 h-20 md:w-24 md:h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon name="Search" size={40} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-3">
                    No Institutes Found
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-md mx-auto">
                    {institutes?.length === 0
                      ? 'No approved institutes are available yet. Check back soon!' :'Try adjusting your filters or search query to discover more institutes'}
                  </p>
                  {institutes?.length > 0 && (
                    <Button variant="outline" onClick={handleClearFilters}>
                      Clear All Filters
                    </Button>
                  )}
                </div>
              ) : (
                <div className={`grid gap-4 md:gap-5 lg:gap-6 ${
                  viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'
                }`}>
                  {filteredInstitutes?.map((institute) => (
                    <InstituteCard
                      key={institute?.id}
                      id={institute?.id}
                      name={institute?.instituteName || institute?.name}
                      logo={institute?.logoUrl || institute?.logo}
                      banner={institute?.bannerUrl || institute?.banner}
                      location={
                        institute?.user?.city
                          ? `${institute?.user?.city}${institute?.user?.state ? ', ' + institute?.user?.state : ''}`
                          : 'Location not specified'
                      }
                      categories={
                        institute?.courses
                          ?.map((c) => c?.category)
                          ?.filter(Boolean)
                          ?.filter((v, i, a) => a?.indexOf(v) === i) || []
                      }
                      coursesOffered={institute?.courses?.length || 0}
                      isSaved={savedInstituteIds?.includes(institute?.id)}
                      onToggleSave={() => handleToggleSave(institute?.id)}
                      instituteUserId={institute?.userId || institute?.user?.id}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className={`lg:col-span-4 space-y-6 ${showSidebar ? 'block' : 'hidden lg:block'}`}>
              <SavedInstitutesPanel
                savedInstitutes={savedInstitutesData}
                onRemove={handleRemoveSaved}
              />

              <UpcomingSessionsPanel sessions={upcomingSessions} />

              <AnnouncementsPanel announcements={announcements} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentDashboard;